"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { localeFromPath, dirFromLocale, type Locale } from "@/config/i18n";
import { localeAlternatePath, localeHasPage } from "@/config/localeRoutes";
import { getPreferredLocale } from "./preferredLocale";

/**
 * Cohérence de langue côté client. Le script inline du <head> (layout.tsx) ne
 * tourne qu'au CHARGEMENT COMPLET — pas en navigation client, ni au bouton
 * « retour » (popstate), ni au restore bfcache. Ce composant comble ces trous :
 *
 *  1. Resynchronise `<html lang/dir>` à chaque navigation + au restore bfcache.
 *  2. Redirige vers la langue PRÉFÉRÉE (choix explicite mémorisé via le
 *     switcher) si la page courante n'est pas dans cette langue ET qu'un vrai
 *     jumeau existe → un visiteur qui revient retrouve « sa » langue.
 *
 * SEO-safe : la redirection est conditionnée à une préférence en localStorage.
 * Un bot (ou un 1er visiteur) n'en a pas → JAMAIS redirigé → chaque version
 * linguistique reste crawlée/indexée normalement. (≠ redirection selon la
 * langue du navigateur, déconseillée par Google et NON utilisée ici.)
 */

const syncHtmlLang = (path: string): Locale => {
  const locale = localeFromPath(path);
  const el = document.documentElement;
  if (el.lang !== locale) el.lang = locale;
  const dir = dirFromLocale(locale);
  if (el.dir !== dir) el.dir = dir;
  return locale;
};

export function LocalePreference() {
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const path = pathname ?? "/";
    const current = syncHtmlLang(path);

    const pref = getPreferredLocale();
    if (!pref || pref === current) return;
    if (!localeHasPage(path, pref)) return; // pas de jumeau traduit → on reste

    const target = localeAlternatePath(path, pref);
    const prefRoot = pref === "fr" ? "/" : `/${pref}`;
    const onHome = path === "/" || path === "/en" || path === "/ar";
    // On ne redirige que vers un VRAI jumeau. Si la page n'a pas d'équivalent,
    // localeAlternatePath retombe sur l'accueil de la langue : on ne veut pas
    // éjecter l'utilisateur de sa page → on ne redirige vers cet accueil que
    // si on est déjà sur un accueil.
    if (target && target !== path && (target !== prefRoot || onHome)) {
      router.replace(target);
    }
  }, [pathname, router]);

  // Restore bfcache (retour navigateur/mobile) : le DOM est restauré tel quel,
  // les effets React ne re-tournent pas → on resync lang/dir via 'pageshow'.
  useEffect(() => {
    const onShow = () => syncHtmlLang(window.location.pathname);
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  return null;
}
